import type { FormEvent } from "react";
import { useEffect, useState } from "react";
import type { AdminOccurrenceDetailDto } from "@madamuh/domain";
import { Link, useParams } from "react-router-dom";
import {
  applyAdminOccurrenceOverride,
  fetchAdminOccurrenceDetail,
  patchAdminOccurrence
} from "../../shared/api/admin-schedule";
import { useSessionBootstrap } from "../../shared/session/SessionBootstrapProvider";
import { PageScaffold } from "../../shared/ui/PageScaffold";

function toDatetimeLocalValue(value: string | null): string {
  if (!value) {
    return "";
  }

  const date = new Date(value);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");

  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

function toIsoString(value: string): string | null {
  if (!value) {
    return null;
  }

  return new Date(value).toISOString();
}

function toCommaSeparatedValue(values: string[]): string {
  return values.join(", ");
}

function parseCommaSeparatedValue(value: string): string[] {
  return value
    .split(",")
    .map((item) => item.trim())
    .filter((item) => item.length > 0);
}

export function AdminOccurrenceDetailPage() {
  const { tenantSlug = "unknown-tenant", occurrenceId = "" } = useParams();
  const { status, session, errorMessage, bootstrapSession } = useSessionBootstrap();
  const [occurrence, setOccurrence] = useState<AdminOccurrenceDetailDto | null>(null);
  const [loadState, setLoadState] = useState<"idle" | "loading" | "error">("idle");
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveState, setSaveState] = useState<"idle" | "saving">("idle");
  const hasAdminRole = Boolean(session?.member?.roles.includes("admin"));

  const [editStartAt, setEditStartAt] = useState("");
  const [editEndAt, setEditEndAt] = useState("");
  const [studioId, setStudioId] = useState("");
  const [studioDecisionStatus, setStudioDecisionStatus] = useState("");
  const [teacherPlatformUserIds, setTeacherPlatformUserIds] = useState("");
  const [teacherAssignmentStatus, setTeacherAssignmentStatus] = useState("");
  const [publicNote, setPublicNote] = useState("");
  const [operatorNote, setOperatorNote] = useState("");
  const [adminNote, setAdminNote] = useState("");
  const [overrideType, setOverrideType] = useState("reschedule");
  const [causeType, setCauseType] = useState("clubDecision");
  const [newDate, setNewDate] = useState("");
  const [newStartAt, setNewStartAt] = useState("");
  const [newEndAt, setNewEndAt] = useState("");
  const [overridePublicNote, setOverridePublicNote] = useState("");
  const [overrideOperatorNote, setOverrideOperatorNote] = useState("");
  const [overrideAdminNote, setOverrideAdminNote] = useState("");

  function syncOccurrenceState(nextOccurrence: AdminOccurrenceDetailDto) {
    setOccurrence(nextOccurrence);
    setEditStartAt(toDatetimeLocalValue(nextOccurrence.startAt));
    setEditEndAt(toDatetimeLocalValue(nextOccurrence.endAt));
    setStudioId(nextOccurrence.studioId ?? "");
    setStudioDecisionStatus(nextOccurrence.studioDecisionStatus ?? "");
    setTeacherPlatformUserIds(toCommaSeparatedValue(nextOccurrence.teacherPlatformUserIds));
    setTeacherAssignmentStatus(nextOccurrence.teacherAssignmentStatus ?? "");
    setPublicNote(nextOccurrence.publicNote ?? "");
    setOperatorNote(nextOccurrence.operatorNote ?? "");
    setAdminNote(nextOccurrence.adminNote ?? "");
    setOverridePublicNote(nextOccurrence.publicNote ?? "");
    setOverrideOperatorNote(nextOccurrence.operatorNote ?? "");
    setOverrideAdminNote(nextOccurrence.adminNote ?? "");
    setNewDate(nextOccurrence.occurrenceDate);
    setNewStartAt(toDatetimeLocalValue(nextOccurrence.startAt));
    setNewEndAt(toDatetimeLocalValue(nextOccurrence.endAt));
  }

  useEffect(() => {
    if (status === "idle") {
      void bootstrapSession(tenantSlug);
    }
  }, [bootstrapSession, status, tenantSlug]);

  useEffect(() => {
    if (status !== "authenticated" || !hasAdminRole || !occurrenceId) {
      return;
    }

    let cancelled = false;

    async function loadOccurrence() {
      setLoadState("loading");
      setLoadError(null);

      try {
        const nextOccurrence = await fetchAdminOccurrenceDetail(tenantSlug, occurrenceId);

        if (cancelled) {
          return;
        }

        syncOccurrenceState(nextOccurrence);
        setLoadState("idle");
      } catch (error) {
        if (cancelled) {
          return;
        }

        setLoadState("error");
        setLoadError(error instanceof Error ? error.message : "occurrence の取得に失敗しました");
      }
    }

    void loadOccurrence();

    return () => {
      cancelled = true;
    };
  }, [hasAdminRole, occurrenceId, status, tenantSlug]);

  async function handlePatch(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!occurrence) {
      return;
    }

    setSaveState("saving");
    setLoadError(null);

    try {
      const nextStartAt = toIsoString(editStartAt);
      const nextEndAt = toIsoString(editEndAt);
      const nextOccurrence = await patchAdminOccurrence(tenantSlug, occurrence.occurrenceId, {
        expectedRevision: occurrence.revision,
        startAt: nextStartAt ?? occurrence.startAt,
        endAt: nextEndAt ?? occurrence.endAt,
        studioId: studioId.trim() || null,
        studioDecisionStatus: studioDecisionStatus.trim() || null,
        teacherPlatformUserIds: parseCommaSeparatedValue(teacherPlatformUserIds),
        teacherAssignmentStatus: teacherAssignmentStatus.trim() || null,
        publicNote: publicNote || null,
        operatorNote: operatorNote || null,
        adminNote: adminNote || null
      });

      syncOccurrenceState(nextOccurrence);
    } catch (error) {
      setLoadError(error instanceof Error ? error.message : "occurrence patch に失敗しました");
    } finally {
      setSaveState("idle");
    }
  }

  async function handleOverride(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!occurrence) {
      return;
    }

    setSaveState("saving");
    setLoadError(null);

    try {
      const nextNewDate = newDate && newDate !== occurrence.occurrenceDate ? newDate : null;
      const nextNewStartAt = toIsoString(newStartAt);
      const nextNewEndAt = toIsoString(newEndAt);
      const nextOccurrence = await applyAdminOccurrenceOverride(tenantSlug, occurrence.occurrenceId, {
        expectedRevision: occurrence.revision,
        overrideType,
        causeType,
        newDate: nextNewDate,
        newStartAt: nextNewStartAt && nextNewStartAt !== occurrence.startAt ? nextNewStartAt : null,
        newEndAt: nextNewEndAt && nextNewEndAt !== occurrence.endAt ? nextNewEndAt : null,
        publicNote: overridePublicNote || null,
        operatorNote: overrideOperatorNote || null,
        adminNote: overrideAdminNote || null
      });

      syncOccurrenceState(nextOccurrence);
    } catch (error) {
      setLoadError(error instanceof Error ? error.message : "override apply に失敗しました");
    } finally {
      setSaveState("idle");
    }
  }

  const adminLinks = [
    { to: "/t/:tenantSlug/teacher/admin", label: "管理ホーム" },
    { to: `/t/:tenantSlug/teacher/admin/schedule-months/${occurrence?.yearMonth ?? "202604"}`, label: "月次スケジュール" }
  ];

  return (
    <PageScaffold
      title="Admin Occurrence Detail"
      description="occurrence の direct edit と override apply を試せる最小画面です。"
      links={adminLinks}
    >
      <div style={{ display: "grid", gap: 16 }}>
        {status === "loading" ? <p>セッションを確認しています。</p> : null}
        {status === "unauthenticated" ? (
          <p>
            admin 画面には session が必要です。<Link to={`/t/${tenantSlug}/entry`}>/entry</Link> で mock operator login を実行してください。
          </p>
        ) : null}
        {status === "error" ? <p>セッション取得に失敗しました: {errorMessage}</p> : null}
        {status === "authenticated" && !hasAdminRole ? <p>この画面は admin role のみ利用できます。</p> : null}
        {loadState === "loading" ? <p>occurrence を読み込んでいます。</p> : null}
        {loadError ? <p>{loadError}</p> : null}

        {occurrence ? (
          <>
            <section style={{ display: "grid", gap: 8 }}>
              <strong style={{ fontSize: 20 }}>{occurrence.lessonTitle}</strong>
              <div style={{ fontSize: 14, opacity: 0.8 }}>occurrenceId: {occurrence.occurrenceId}</div>
              <div style={{ fontSize: 14, opacity: 0.8 }}>revision: {occurrence.revision}</div>
              <div style={{ fontSize: 14, opacity: 0.8 }}>
                {occurrence.sourceType} / {occurrence.syncState} / {occurrence.resolutionType}
              </div>
              <div style={{ fontSize: 14, opacity: 0.8 }}>{occurrence.startAt.slice(0, 16)} - {occurrence.endAt.slice(0, 16)}</div>
              <div style={{ fontSize: 14, opacity: 0.8 }}>
                studio: {occurrence.studioNameSnapshot ?? "未定"} / {occurrence.studioDecisionStatus ?? "unset"}
              </div>
              <div style={{ fontSize: 14, opacity: 0.8 }}>
                teachers: {occurrence.teacherNameSnapshots.join(", ") || "未定"} / {occurrence.teacherAssignmentStatus ?? "unset"}
              </div>
            </section>

            <form onSubmit={handlePatch} style={{ display: "grid", gap: 12 }}>
              <h2 style={{ margin: 0, fontSize: 18 }}>Direct Edit</h2>
              <label style={{ display: "grid", gap: 4 }}>
                <span>startAt</span>
                <input
                  type="datetime-local"
                  value={editStartAt}
                  onChange={(event) => setEditStartAt(event.target.value)}
                />
              </label>
              <label style={{ display: "grid", gap: 4 }}>
                <span>endAt</span>
                <input type="datetime-local" value={editEndAt} onChange={(event) => setEditEndAt(event.target.value)} />
              </label>
              <label style={{ display: "grid", gap: 4 }}>
                <span>studioId</span>
                <input value={studioId} onChange={(event) => setStudioId(event.target.value)} />
              </label>
              <label style={{ display: "grid", gap: 4 }}>
                <span>studioDecisionStatus</span>
                <input
                  value={studioDecisionStatus}
                  onChange={(event) => setStudioDecisionStatus(event.target.value)}
                />
              </label>
              <label style={{ display: "grid", gap: 4 }}>
                <span>teacherPlatformUserIds</span>
                <input
                  value={teacherPlatformUserIds}
                  onChange={(event) => setTeacherPlatformUserIds(event.target.value)}
                  placeholder="platform_user_teacher_mika, platform_user_teacher_kai"
                />
              </label>
              <label style={{ display: "grid", gap: 4 }}>
                <span>teacherAssignmentStatus</span>
                <input
                  value={teacherAssignmentStatus}
                  onChange={(event) => setTeacherAssignmentStatus(event.target.value)}
                />
              </label>
              <label style={{ display: "grid", gap: 4 }}>
                <span>publicNote</span>
                <textarea value={publicNote} onChange={(event) => setPublicNote(event.target.value)} rows={3} />
              </label>
              <label style={{ display: "grid", gap: 4 }}>
                <span>operatorNote</span>
                <textarea value={operatorNote} onChange={(event) => setOperatorNote(event.target.value)} rows={3} />
              </label>
              <label style={{ display: "grid", gap: 4 }}>
                <span>adminNote</span>
                <textarea value={adminNote} onChange={(event) => setAdminNote(event.target.value)} rows={3} />
              </label>
              <button type="submit" disabled={saveState === "saving"}>
                {saveState === "saving" ? "保存中..." : "PATCH occurrence"}
              </button>
            </form>

            <form onSubmit={handleOverride} style={{ display: "grid", gap: 12 }}>
              <h2 style={{ margin: 0, fontSize: 18 }}>Apply Override</h2>
              <label style={{ display: "grid", gap: 4 }}>
                <span>overrideType</span>
                <input value={overrideType} onChange={(event) => setOverrideType(event.target.value)} />
              </label>
              <label style={{ display: "grid", gap: 4 }}>
                <span>causeType</span>
                <input value={causeType} onChange={(event) => setCauseType(event.target.value)} />
              </label>
              <label style={{ display: "grid", gap: 4 }}>
                <span>newDate</span>
                <input type="date" value={newDate} onChange={(event) => setNewDate(event.target.value)} />
              </label>
              <label style={{ display: "grid", gap: 4 }}>
                <span>newStartAt</span>
                <input
                  type="datetime-local"
                  value={newStartAt}
                  onChange={(event) => setNewStartAt(event.target.value)}
                />
              </label>
              <label style={{ display: "grid", gap: 4 }}>
                <span>newEndAt</span>
                <input type="datetime-local" value={newEndAt} onChange={(event) => setNewEndAt(event.target.value)} />
              </label>
              <label style={{ display: "grid", gap: 4 }}>
                <span>override publicNote</span>
                <textarea
                  value={overridePublicNote}
                  onChange={(event) => setOverridePublicNote(event.target.value)}
                  rows={3}
                />
              </label>
              <label style={{ display: "grid", gap: 4 }}>
                <span>override operatorNote</span>
                <textarea
                  value={overrideOperatorNote}
                  onChange={(event) => setOverrideOperatorNote(event.target.value)}
                  rows={3}
                />
              </label>
              <label style={{ display: "grid", gap: 4 }}>
                <span>override adminNote</span>
                <textarea
                  value={overrideAdminNote}
                  onChange={(event) => setOverrideAdminNote(event.target.value)}
                  rows={3}
                />
              </label>
              <button type="submit" disabled={saveState === "saving"}>
                {saveState === "saving" ? "保存中..." : "POST override"}
              </button>
            </form>

            <section style={{ display: "grid", gap: 8 }}>
              <h2 style={{ margin: 0, fontSize: 18 }}>Applied Overrides</h2>
              {occurrence.overrides.length === 0 ? <p style={{ margin: 0 }}>override はありません。</p> : null}
              {occurrence.overrides.map((override) => (
                <article
                  key={override.overrideId}
                  style={{ border: "1px solid #e5e5e5", borderRadius: 12, padding: 12, display: "grid", gap: 6 }}
                >
                  <strong>{override.overrideType}</strong>
                  <div style={{ fontSize: 14, opacity: 0.8 }}>{override.causeType}</div>
                  <div style={{ fontSize: 14, opacity: 0.8 }}>status: {override.status}</div>
                  {override.newStartAt ? <div style={{ fontSize: 14 }}>newStartAt: {override.newStartAt}</div> : null}
                  {override.newEndAt ? <div style={{ fontSize: 14 }}>newEndAt: {override.newEndAt}</div> : null}
                  {override.publicNote ? <div style={{ fontSize: 14 }}>publicNote: {override.publicNote}</div> : null}
                </article>
              ))}
            </section>
          </>
        ) : null}
      </div>
    </PageScaffold>
  );
}
